import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, ChevronRight, ChevronDown, ArrowLeft, Percent, Info, Search, X, GitBranch, FolderTree } from 'lucide-react';
import * as api from '../../services/api';
import { useTariffTree } from '../../hooks/useTariffTree';

// ── Build nested tree from flat nodes ────────────────

function buildTree(flatNodes) {
  if (!flatNodes || flatNodes.length === 0) return [];

  // Group nodes by parent_id
  const byParent = new Map();
  const nodeMap = new Map();

  for (const n of flatNodes) {
    nodeMap.set(n.hs_id, n);
    const pid = n.parent_id;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(n);
  }

  // Find root parent_id (nodes whose parent is not in our set)
  const allIds = new Set(flatNodes.map(n => n.hs_id));

  function buildChildren(parentId) {
    const children = byParent.get(parentId) || [];
    return children.map(child => ({
      ...child,
      children: (child.expanded || child.is_current) ? buildChildren(child.hs_id) : [],
    }));
  }

  // Find the root parent_ids — those parent_ids that are NOT in our node set
  const rootParentIds = new Set();
  for (const n of flatNodes) {
    if (!allIds.has(n.parent_id)) {
      rootParentIds.add(n.parent_id);
    }
  }

  let result = [];
  for (const pid of rootParentIds) {
    result = result.concat(buildChildren(pid));
  }
  return result;
}

// ── Tree connector guides (CSS-based) ───────────────
//
// Renders ancestor verticals + this row's elbow (L-shape) as
// absolutely-positioned spans inside a single relative wrapper.
// `ancestors` is [{ isLast, isActive }] for each ancestor at depth 1..depth-1.
//
// Visual semantics:
//   - Vertical line at column i  → ancestor at depth i+1 was NOT last child
//   - Elbow at column `depth-1`  → this row's own join point (border-l + border-b)
//   - Continuation below elbow   → present only if this row is NOT last child
//   - Active path                → blue (ancestor of current code)
//                                  emerald (current code itself)

function TreeGuides({ depth, isLast, ancestors, isCurrent, isAncestor }) {
  if (depth === 0) return null;

  const isOnPath = isCurrent || isAncestor;
  const elbowCls = isCurrent
    ? 'border-emerald-400 dark:border-emerald-500'
    : isAncestor
      ? 'border-blue-400 dark:border-blue-500'
      : 'border-border-strong/70';

  return (
    <span
      className="relative flex-shrink-0 self-stretch select-none"
      style={{ width: `calc(${depth} * var(--tree-indent))` }}
      aria-hidden="true"
    >
      {/* Ancestor verticals at columns 0..depth-2 */}
      {ancestors.map((anc, s) => {
        if (anc.isLast) return null;
        const cls = anc.isActive
          ? 'border-blue-400/70 dark:border-blue-500/70'
          : 'border-border-strong/40';
        return (
          <span
            key={s}
            className={`absolute top-0 bottom-0 border-l ${cls}`}
            style={{ left: `calc((${s} + 0.5) * var(--tree-indent))` }}
          />
        );
      })}

      {/* Elbow at column depth-1: top→mid vertical + mid horizontal stub */}
      <span
        className={`absolute border-l border-b ${elbowCls} ${isOnPath ? '' : ''}`}
        style={{
          left: `calc((${depth} - 0.5) * var(--tree-indent))`,
          top: 0,
          width: `calc(0.5 * var(--tree-indent))`,
          height: '50%',
          borderBottomLeftRadius: '3px',
        }}
      />

      {/* Continuation below elbow if not last sibling */}
      {!isLast && (
        <span
          className={`absolute border-l ${elbowCls}`}
          style={{
            left: `calc((${depth} - 0.5) * var(--tree-indent))`,
            top: '50%',
            bottom: 0,
          }}
        />
      )}
    </span>
  );
}

// ── HS Code badge ────────────────────────────────────

function CodeBadge({ code, isCurrent, isAncestor, onClick }) {
  if (!code) return null;

  const isLeaf = code.length === 10;

  let cls = 'font-mono px-1 py-0.5 rounded font-medium inline-block flex-shrink-0 ';
  const badgeStyle = { fontSize: 'var(--tree-font-size-sm)' };
  if (isCurrent) {
    cls += 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-700';
  } else if (isAncestor) {
    cls += 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300';
  } else if (isLeaf) {
    cls += 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300';
  } else {
    cls += 'bg-app text-text-muted';
  }

  return (
    <button
      onClick={onClick}
      className={`${cls} hover:opacity-80 cursor-pointer transition-opacity`}
      style={badgeStyle}
      title={`Перейти до ${code}`}
    >
      {code}
    </button>
  );
}

// ── Inline suffix: duty + unit after name text ───────

function NodeSuffix({ dutyDisplay, unit }) {
  if (!dutyDisplay && !unit) return null;
  return (
    <>
      {dutyDisplay && (
        <span className="text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/30 px-1 py-0 rounded font-medium whitespace-nowrap ml-1"
              style={{ fontSize: 'var(--tree-font-size-sm)' }}>
          {dutyDisplay}
        </span>
      )}
      {unit && (
        <span className="text-text-disabled whitespace-nowrap ml-1 border border-border-default rounded px-0.5 py-0"
              style={{ fontSize: 'var(--tree-font-size-xs)' }}>
          од: {unit}
        </span>
      )}
    </>
  );
}

// ── Normalize tree filter input ──────────────────────

// "3304 20 00 00" → "3304200000". Only digits+whitespace input is treated as a code;
// anything with letters (descriptions) is left untouched.
export function normalizeTreeFilter(value) {
  if (/^[\d\s]+$/.test(value) && /\d/.test(value)) {
    return value.replace(/\s+/g, '');
  }
  return value;
}

// ── Highlight matching text ──────────────────────────

function HighlightText({ text, query }) {
  if (!query || !text) return text || null;
  const q = query.toLowerCase();
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-inherit dark:bg-yellow-500/30 dark:text-yellow-100 rounded-sm px-0">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Single tree node (recursive) ─────────────────────

function TreeNode({ node, depth = 0, isLast = false, ancestors = [], onNavigate, onExpand, onToggle, currentRef, searchQuery, treeMode }) {
  const isCurrent = node.is_current;
  const isAncestor = node.is_ancestor;
  const isOnPath = isCurrent || isAncestor;
  const hasCode = node.code && node.code.length > 0;
  const hasChildren = node.children && node.children.length > 0;
  const canExpand = node.has_children && !hasChildren && !node.expanded;

  const handleClick = () => {
    if (treeMode === 'explorer' && node.has_children) {
      // In explorer mode, clicking a branch node toggles expand/collapse
      if (onToggle) onToggle(node.hs_id);
    } else if (hasCode) {
      onNavigate(node.code);
    } else if (node.hs_id) {
      onExpand(node.hs_id);
    }
  };

  const handleCodeClick = () => {
    // Code badge always navigates (even in explorer mode)
    if (hasCode) onNavigate(node.code);
  };

  return (
    <>
      <div
        ref={isCurrent ? currentRef : null}
        className={`flex items-stretch gap-1 py-[3px] pr-1 leading-snug transition-colors
          ${isCurrent
            ? 'bg-emerald-50 dark:bg-emerald-950/30 rounded-md -mx-1 px-1'
            : isAncestor
              ? ''
              : 'hover:bg-hover rounded-md -mx-1 px-1'
          }`}
        style={{ fontSize: 'var(--tree-font-size)' }}
      >
        {/* Tree connector guides (ancestor verticals + this row's elbow) */}
        <TreeGuides
          depth={depth}
          isLast={isLast}
          ancestors={ancestors}
          isCurrent={isCurrent}
          isAncestor={isAncestor}
        />

        {/* Inner content: chevron + badge + name. items-start so badge aligns to first line. */}
        <div className="flex items-start gap-1 min-w-0 flex-1">
          {/* Expand/collapse icon for nodes with children */}
          {(node.has_children) ? (
            <button
              onClick={treeMode === 'explorer' && onToggle ? () => onToggle(node.hs_id) : handleClick}
              className="mt-[1px] flex-shrink-0 text-text-disabled hover:text-text-secondary transition-colors"
            >
              {hasChildren || node.expanded
                ? <ChevronDown style={{ width: 'var(--tree-icon)', height: 'var(--tree-icon)' }} />
                : <ChevronRight style={{ width: 'var(--tree-icon)', height: 'var(--tree-icon)' }} />
              }
            </button>
          ) : (
            <span className="flex-shrink-0" style={{ width: 'var(--tree-icon)' }} />
          )}

          {/* Code badge */}
          {hasCode && (
            <CodeBadge
              code={node.code}
              isCurrent={isCurrent}
              isAncestor={isAncestor}
              onClick={handleCodeClick}
            />
          )}

          {/* Name + duty + unit (all inline, wrapping naturally) */}
          <span
            lang="uk"
            className={`min-w-0 flex-1 break-words hyphens-auto ${
              isCurrent ? 'text-emerald-800 dark:text-emerald-300 font-medium' :
              isAncestor ? 'text-blue-700 dark:text-blue-300' :
              'text-text-secondary'
            } ${node.has_children && (hasCode || treeMode === 'explorer') ? 'cursor-pointer hover:underline' : ''}`}
            style={{ overflowWrap: 'anywhere' }}
            onClick={node.has_children && (hasCode || treeMode === 'explorer') ? handleClick : undefined}
          >
            <HighlightText text={node.name} query={searchQuery} />
            <NodeSuffix dutyDisplay={node.duty_display} unit={node.unit} />
          </span>
        </div>
      </div>

      {/* Render children recursively */}
      {hasChildren && node.children.map((child, i) => (
        <TreeNode
          key={child.hs_id}
          node={child}
          depth={depth + 1}
          isLast={i === node.children.length - 1}
          ancestors={[...ancestors, { isLast, isActive: isOnPath }]}
          onNavigate={onNavigate}
          onExpand={onExpand}
          onToggle={onToggle}
          currentRef={currentRef}
          searchQuery={searchQuery}
          treeMode={treeMode}
        />
      ))}
    </>
  );
}

// ── FTA rates collapsible list (for DetailPanel header) ──

function FtaRatesList({ ftaRates }) {
  if (!ftaRates || ftaRates.length === 0) return null;
  return (
    <details className="group/fta mt-0.5">
      <summary className="flex items-center gap-1 text-text-disabled cursor-pointer hover:text-text-secondary select-none"
               style={{ fontSize: 'var(--tree-font-size-sm)' }}>
        <ChevronRight className="w-2.5 h-2.5 transition-transform group-open/fta:rotate-90" />
        Вільна торгівля ({ftaRates.length})
      </summary>
      <div className="mt-0.5 space-y-0">
        {ftaRates.map((t, i) => {
          const shortName = t.type
            .replace('Ввізне мито (Вільна торгівля з ', '')
            .replace(')', '')
            .trim();
          return (
            <div key={`fta-${i}`} className="flex items-center justify-between py-0"
                 style={{ fontSize: 'var(--tree-font-size-sm)' }}>
              <span className="text-text-disabled truncate mr-2">{shortName}</span>
              <span className={`font-medium flex-shrink-0 ${
                t.rate_full === 0 && t.rate_full_specific === 0 ? 'text-green-600 dark:text-green-400' : 'text-text-muted'
              }`}>
                {t.display}
              </span>
            </div>
          );
        })}
      </div>
    </details>
  );
}

// ── Exported for use in DetailPanel header ──
export { FtaRatesList };

// ── Main TariffPanel component ───────────────────────

export default function TariffPanel({ code, onCountChange, onTariffInfo, onActiveCodeChange }) {
  const {
    tree, loading: treeLoading, error: treeError,
    getNodeByCode, getNodeById, getAncestorPath,
    buildVisibleNodes, buildAncestorOnlyNodes, buildExplorerNodes, buildFullNestedTree,
  } = useTariffTree();

  const [viewCode, setViewCode] = useState(code);
  const [viewNodeId, setViewNodeId] = useState(null);
  const [history, setHistory] = useState([]);
  const [treeFilter, setTreeFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [ancestorsOnly, setAncestorsOnly] = useState(false);
  const [treeMode, setTreeMode] = useState('focus'); // 'focus' | 'explorer'
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [ratesLoading, setRatesLoading] = useState(false);
  const currentNodeRef = useRef(null);
  const filterInputRef = useRef(null);
  const ratesCacheRef = useRef(new Map()); // code -> rates data
  const prevViewNodeIdRef = useRef(null); // track navigation for scroll logic
  const prevAncestorsOnlyRef = useRef(false); // detect filter toggles in scroll logic

  // Debounce filter input (300ms) for full-tree search performance
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(treeFilter), 300);
    return () => clearTimeout(t);
  }, [treeFilter]);

  // Reset when external code changes
  useEffect(() => {
    setViewCode(code);
    setHistory([]);
    setTreeFilter('');
    setDebouncedFilter('');
    setAncestorsOnly(false);
    setExpandedNodes(new Set());
  }, [code]);

  // Resolve viewCode → nodeId and call onTariffInfo
  useEffect(() => {
    if (!tree || !viewCode) return;

    let nodeId;
    if (viewCode.startsWith('#')) {
      nodeId = parseInt(viewCode.slice(1));
    } else {
      const node = getNodeByCode(viewCode);
      if (node) nodeId = node.hs_id;
    }

    if (nodeId == null) return;
    setViewNodeId(nodeId);

    // Get node data from cache
    const nodeObj = tree.nodeById.get(nodeId);
    if (!nodeObj) return;

    if (onActiveCodeChange) {
      onActiveCodeChange(nodeObj.code || '');
    }

    // Immediately call onTariffInfo with tree-cached data
    if (onTariffInfo) {
      onTariffInfo({
        name: nodeObj.name || null,
        unit: nodeObj.unit || null,
        unitAdditional: nodeObj.unit_additional || null,
        dutyDisplay: nodeObj.duty_display || null,
        ftaRates: [],
      });
    }

    // Fetch FTA rates for 10-digit codes
    if (nodeObj.code?.length === 10) {
      const cached = ratesCacheRef.current.get(nodeObj.code);
      if (cached) {
        // Use cached rates
        if (onCountChange) onCountChange(cached.tariffs?.length || 0);
        if (onTariffInfo) {
          const ftaRates = (cached.tariffs || []).filter(t =>
            t.type_code !== 10 && t.type && t.type.includes('Вільна торгівля')
          );
          onTariffInfo({
            name: nodeObj.name,
            unit: cached.unit || nodeObj.unit,
            unitAdditional: cached.unit_additional || nodeObj.unit_additional,
            dutyDisplay: nodeObj.duty_display,
            ftaRates,
          });
        }
      } else {
        setRatesLoading(true);
        api.getTariffRates(nodeObj.code)
          .then(result => {
            ratesCacheRef.current.set(nodeObj.code, result);
            if (onCountChange) onCountChange(result.tariffs?.length || 0);
            if (onTariffInfo) {
              const ftaRates = (result.tariffs || []).filter(t =>
                t.type_code !== 10 && t.type && t.type.includes('Вільна торгівля')
              );
              onTariffInfo({
                name: nodeObj.name,
                unit: result.unit || nodeObj.unit,
                unitAdditional: result.unit_additional || nodeObj.unit_additional,
                dutyDisplay: nodeObj.duty_display,
                ftaRates,
              });
            }
          })
          .catch(err => console.error('Failed to load tariff rates:', err))
          .finally(() => setRatesLoading(false));
      }
    } else {
      if (onCountChange) onCountChange(0);
    }
  }, [tree, viewCode, getNodeByCode, onTariffInfo, onCountChange, onActiveCodeChange]);

  // In explorer mode, auto-expand ancestors when viewNodeId changes
  useEffect(() => {
    if (treeMode === 'explorer' && viewNodeId != null && tree) {
      const path = getAncestorPath(viewNodeId);
      setExpandedNodes(prev => {
        const next = new Set(prev);
        for (const anc of path) {
          next.add(anc.hs_id);
        }
        return next;
      });
    }
  }, [treeMode, viewNodeId, tree, getAncestorPath]);

  const handleNavigate = useCallback((newCode) => {
    if (newCode && newCode !== viewCode) {
      setHistory(h => [...h, viewCode]);
      setViewCode(newCode);
      // Clear search filter on navigation so tree shows the new code in context
      if (treeFilter) {
        setTreeFilter('');
        setDebouncedFilter('');
      }
    }
  }, [viewCode, treeFilter]);

  const handleBack = useCallback(() => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(h => h.slice(0, -1));
      setViewCode(prev);
    }
  }, [history]);

  const handleBackToOriginal = useCallback(() => {
    setHistory([]);
    setViewCode(code);
  }, [code]);

  const handleExpandNode = useCallback((hsId) => {
    setHistory(h => [...h, viewCode]);
    setViewCode(`#${hsId}`);
    if (treeFilter) {
      setTreeFilter('');
      setDebouncedFilter('');
    }
  }, [viewCode, treeFilter]);

  // Explorer mode: toggle expand/collapse of a node
  const handleToggle = useCallback((hsId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(hsId)) {
        next.delete(hsId);
      } else {
        next.add(hsId);
      }
      return next;
    });
  }, []);

  // Switch to explorer mode: initialize expanded set from current focus view
  const handleSwitchToExplorer = useCallback(() => {
    if (viewNodeId != null && tree) {
      const path = getAncestorPath(viewNodeId);
      setExpandedNodes(new Set(path.map(n => n.hs_id)));
    }
    setTreeMode('explorer');
  }, [viewNodeId, tree, getAncestorPath]);

  const handleSwitchToFocus = useCallback(() => {
    setTreeMode('focus');
  }, []);

  // ancestorsOnly takes precedence in both modes — when on, show just the
  // chain (root → current) regardless of explorer's expanded set, which
  // remains preserved for when the filter is toggled off again.
  const effectiveTreeMode = ancestorsOnly ? 'focus' : treeMode;
  const treeRoots = useMemo(() => {
    if (!tree || viewNodeId == null) return [];
    let flatNodes;
    if (ancestorsOnly) {
      flatNodes = buildAncestorOnlyNodes(viewNodeId);
    } else if (treeMode === 'explorer') {
      flatNodes = buildExplorerNodes(viewNodeId, expandedNodes);
    } else {
      flatNodes = buildVisibleNodes(viewNodeId);
    }
    return buildTree(flatNodes);
  }, [tree, viewNodeId, treeMode, expandedNodes, ancestorsOnly, buildVisibleNodes, buildAncestorOnlyNodes, buildExplorerNodes]);

  // Scroll to current node after tree renders
  // In focus mode: scroll on every tree rebuild (viewNodeId or treeRoots change)
  // In explorer mode: only scroll when viewNodeId actually changes (navigation),
  // NOT when treeRoots changes due to expand/collapse of branches
  useEffect(() => {
    if (viewNodeId == null || treeRoots.length === 0) return;

    const ancestorsOnlyChanged = prevAncestorsOnlyRef.current !== ancestorsOnly;
    prevAncestorsOnlyRef.current = ancestorsOnly;

    if (treeMode === 'explorer' && !ancestorsOnly && !ancestorsOnlyChanged) {
      // Pure explorer rebuild without filter toggle — likely just a branch
      // expand/collapse. Skip scrolling so the user's reading position is
      // preserved. We still scroll on actual navigation (viewNodeId change)
      // and on filter on/off transitions.
      if (prevViewNodeIdRef.current === viewNodeId) return;
    }
    prevViewNodeIdRef.current = viewNodeId;

    requestAnimationFrame(() => {
      currentNodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [viewNodeId, treeRoots, treeMode, ancestorsOnly]);

  // Full nested tree for global search (memoized separately, expensive)
  const activeFilter = debouncedFilter.trim().length >= 4 ? debouncedFilter.trim() : '';
  const fullNestedTree = useMemo(() => {
    if (!activeFilter || !tree) return null;
    return buildFullNestedTree();
  }, [activeFilter, tree, buildFullNestedTree]);

  // Filter tree nodes by search text
  const filteredTreeRoots = useMemo(() => {
    if (!activeFilter) return treeRoots;
    const q = activeFilter.toLowerCase();

    function nodeMatches(node) {
      const nameMatch = node.name && node.name.toLowerCase().includes(q);
      const codeMatch = node.code && node.code.startsWith(q);
      return nameMatch || codeMatch;
    }

    function filterNodes(nodes) {
      return nodes.reduce((acc, node) => {
        const selfMatch = nodeMatches(node);
        const filteredChildren = node.children ? filterNodes(node.children) : [];
        if (selfMatch || filteredChildren.length > 0) {
          acc.push({ ...node, children: selfMatch ? (node.children || []) : filteredChildren });
        }
        return acc;
      }, []);
    }

    // When filtering, search the ENTIRE tree (all 17K nodes)
    const sourceRoots = fullNestedTree || treeRoots;
    return filterNodes(sourceRoots);
  }, [treeRoots, activeFilter, fullNestedTree]);

  // Get current viewCode for info hint
  const currentViewNode = tree && viewNodeId != null ? tree.nodeById.get(viewNodeId) : null;
  const currentViewCode = currentViewNode?.code || '';

  if (treeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500 dark:text-blue-400" />
      </div>
    );
  }

  if (treeError) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-500 dark:text-red-400">{treeError}</p>
      </div>
    );
  }

  if (!tree) return null;

  const isNavigated = viewCode !== code;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2">
      {/* Pinned: Navigation bar */}
      {isNavigated && (
        <div className="flex-shrink-0 flex items-center gap-2 bg-app rounded-lg px-2.5 py-1.5">
          <button
            onClick={handleBack}
            className="p-1 hover:bg-hover rounded transition-colors"
            title="Назад"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-text-muted" />
          </button>
          <button
            onClick={handleBackToOriginal}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            style={{ fontSize: 'var(--tree-font-size-sm)' }}
          >
            ← {code}
          </button>
          {ratesLoading && <Loader2 className="w-3 h-3 animate-spin text-blue-500 dark:text-blue-400 ml-auto" />}
        </div>
      )}

      {/* Pinned: Search input + ancestors toggle */}
      <div className="flex-shrink-0 flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-text-disabled" />
          <input
            ref={filterInputRef}
            type="text"
            value={treeFilter}
            onChange={(e) => setTreeFilter(normalizeTreeFilter(e.target.value))}
            placeholder="Пошук в дереві..."
            className="w-full pl-7 pr-7 py-1.5 border border-border-default rounded-md bg-app text-text-primary
                       focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-ring-focus
                       placeholder:text-text-disabled"
            style={{ fontSize: 'var(--tree-font-size)' }}
          />
          {treeFilter && (
            <button
              onClick={() => { setTreeFilter(''); setDebouncedFilter(''); filterInputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-disabled hover:text-text-secondary"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <button
          onClick={() => setAncestorsOnly(prev => !prev)}
          className={`flex-shrink-0 p-1.5 rounded-md border transition-colors
            ${ancestorsOnly
              ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/40 dark:border-blue-900/60 dark:text-blue-300'
              : 'bg-app border-border-default text-text-disabled hover:bg-hover hover:text-text-secondary'
            }`}
          title={ancestorsOnly ? 'Показати все дерево' : 'Тільки шлях (предки)'}
        >
          <GitBranch className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={treeMode === 'explorer' ? handleSwitchToFocus : handleSwitchToExplorer}
          className={`flex-shrink-0 p-1.5 rounded-md border transition-colors
            ${treeMode === 'explorer'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900/60 dark:text-emerald-300'
              : 'bg-app border-border-default text-text-disabled hover:bg-hover hover:text-text-secondary'
            }`}
          title={treeMode === 'explorer' ? 'Режим фокусу (автоматичний)' : 'Режим провідника (вільна навігація)'}
        >
          <FolderTree className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Info hint for non-leaf codes */}
      {currentViewCode && currentViewCode.length > 0 && currentViewCode.length < 10 && (
        <div className="flex-shrink-0 flex items-center gap-1.5 text-text-disabled px-1"
             style={{ fontSize: 'var(--tree-font-size-sm)' }}>
          <Info className="w-3 h-3 flex-shrink-0" />
          Ставки мита доступні для 10-значних кодів
        </div>
      )}

      {/* Scrollable tree view */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
           style={{ fontSize: 'var(--tree-font-size)' }}>
        {filteredTreeRoots.map((root, i) => (
          <TreeNode
            key={root.hs_id}
            node={root}
            depth={0}
            isLast={i === filteredTreeRoots.length - 1}
            onNavigate={handleNavigate}
            onExpand={handleExpandNode}
            onToggle={effectiveTreeMode === 'explorer' ? handleToggle : undefined}
            currentRef={currentNodeRef}
            searchQuery={activeFilter}
            treeMode={effectiveTreeMode}
          />
        ))}
        {activeFilter && filteredTreeRoots.length === 0 && (
          <div className="text-text-disabled text-center py-3" style={{ fontSize: 'var(--tree-font-size-sm)' }}>
            Нічого не знайдено
          </div>
        )}
      </div>
    </div>
  );
}
